import { observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import type { UserResponseArgs } from '../user/user2';
import User, { convertUserResponseToArgs } from '../user/user2';
import type { TravelerInformationResponseArgs } from '../traveler_information';
import TravelerInformation, { convertTravelerInformationResponseToArgs } from '../traveler_information';
import type { InAdvanceApprovalResponseArgs } from '../in_advance_approval';
import { convertInAdvanceApprovalResponseToArgs, InAdvanceApproval } from '../in_advance_approval';
import type { ArrangementRequestResponseArgs } from '../arrangement_requiest';
import ArrangementRequest, { convertArrangementRequestResponseToArgs } from '../arrangement_requiest';
import type { OrderResponseArgs } from '../order2';
import Order, { convertOrderResponseToArgs } from '../order2';
import type { NonOrderItemResponseArgs } from '../non_order_item2';
import NonOrderItem, { convertNonOrderItemResponseToArgs } from '../non_order_item2';
import type { TripRuleErrorArgs } from './trip_rule_error';
import TripRuleError from './trip_rule_error';

export type TripResponseArgs = {
  id: number;
  user: UserResponseArgs;
  search_query_id: number;
  cache_start_time: string;
  cache_end_time: string;
  peoplenum: number;
  final_destination: string;
  purpose: string;
  internal_number: string;
  flight_approve_expired_at?: string;
  current_order?: OrderResponseArgs;
  first_order?: OrderResponseArgs;
  applied_order?: OrderResponseArgs;
  traveler_informations: TravelerInformationResponseArgs[];
  cache_rule_errors: TripRuleErrorArgs[];
  in_advance_approval: InAdvanceApprovalResponseArgs[]; // serverから単数形で返ってくるのでここで吸収する
  current_approval_stage: number;
  arrangement_request: ArrangementRequestResponseArgs;
  non_order_items: NonOrderItemResponseArgs[];
  received_at_in_tokyo?: string;
  created_at: string;
};

export type TripArgs = {
  id: number;
  user: User;
  showFee: boolean;
  searchQueryId: number;
  cacheStartTime: Moment;
  cacheEndTime: Moment;
  peoplenum: number;
  finalDestination: string;
  purpose: string;
  internalNumber: string;
  flightApproveExpiredAt: Moment | null;
  order?: Order;
  firstOrder?: Order;
  appliedOrder?: Order;
  travelerInformations: TravelerInformation[];
  ruleErrors: TripRuleError[];
  inAdvanceApprovals: InAdvanceApproval[];
  currentApprovalStage: number;
  arrangementRequest: ArrangementRequest | null;
  nonOrderItems: NonOrderItem[];
  receivedAtInTokyo?: Moment;
  createdAt: Moment;
};

export const convertTripResponseToArgs = (response: TripResponseArgs, showFee: boolean) => {
  const args: TripArgs = {
    id: response.id,
    user: new User(convertUserResponseToArgs(response.user)),
    showFee,
    searchQueryId: response.search_query_id,
    cacheStartTime: moment(response.cache_start_time),
    cacheEndTime: moment(response.cache_end_time),
    peoplenum: response.peoplenum,
    finalDestination: response.final_destination,
    purpose: response.purpose,
    internalNumber: response.internal_number,
    flightApproveExpiredAt: response.flight_approve_expired_at ? moment(response.flight_approve_expired_at) : null,
    order: response.current_order
      ? new Order(convertOrderResponseToArgs(response.current_order, showFee))
      : undefined,
    firstOrder: response.first_order
      ? new Order(convertOrderResponseToArgs(response.first_order, showFee))
      : undefined,
    appliedOrder: response.applied_order
      ? new Order(convertOrderResponseToArgs(response.applied_order, showFee))
      : undefined,
    travelerInformations: response.traveler_informations.map(
      travelerInformation => new TravelerInformation(convertTravelerInformationResponseToArgs(travelerInformation))
    ),
    ruleErrors: response.cache_rule_errors.map(cacheRuleError => new TripRuleError(cacheRuleError)),
    inAdvanceApprovals: response.in_advance_approval.map(
      inAdvanceApproval => new InAdvanceApproval(convertInAdvanceApprovalResponseToArgs(inAdvanceApproval))
    ),
    currentApprovalStage: response.current_approval_stage,
    arrangementRequest: response.arrangement_request
      ? new ArrangementRequest(convertArrangementRequestResponseToArgs(response.arrangement_request))
      : null,
    nonOrderItems: response.non_order_items.map(
      nonOrderItem => new NonOrderItem(convertNonOrderItemResponseToArgs(nonOrderItem))
    ),
    receivedAtInTokyo: response.received_at_in_tokyo ? moment(response.received_at_in_tokyo) : undefined,
    createdAt: moment(response.created_at)
  };
  return args;
};

export default class Trip {
  @observable
  id: number;

  @observable
  user: User;

  @observable
  showFee: boolean;

  @observable
  searchQueryId: number;

  @observable
  cacheStartTime: Moment;

  @observable
  cacheEndTime: Moment;

  @observable
  peoplenum: number;

  @observable
  finalDestination: string;

  @observable
  purpose: string;

  @observable
  internalNumber: string;

  @observable
  flightApproveExpiredAt: Moment | null;

  @observable
  order?: Order;

  @observable
  firstOrder?: Order;

  @observable
  appliedOrder?: Order;

  @observable
  travelerInformations: TravelerInformation[];

  @observable
  ruleErrors: TripRuleError[];

  @observable
  inAdvanceApprovals: InAdvanceApproval[];

  @observable
  currentApprovalStage: number;

  @observable
  arrangementRequest: ArrangementRequest | null;

  @observable
  nonOrderItems: NonOrderItem[];

  @observable
  receivedAtInTokyo?: Moment;

  @observable
  createdAt: Moment;

  constructor(args: TripArgs) {
    this.id = args.id;
    this.user = args.user;
    this.showFee = args.showFee;
    this.searchQueryId = args.searchQueryId;
    this.cacheStartTime = args.cacheStartTime;
    this.cacheEndTime = args.cacheEndTime;
    this.peoplenum = args.peoplenum;
    this.finalDestination = args.finalDestination;
    this.purpose = args.purpose;
    this.internalNumber = args.internalNumber;
    this.flightApproveExpiredAt = args.flightApproveExpiredAt;
    this.order = args.order;
    this.firstOrder = args.firstOrder;
    this.appliedOrder = args.appliedOrder;
    this.travelerInformations = args.travelerInformations;
    this.ruleErrors = args.ruleErrors;
    this.inAdvanceApprovals = args.inAdvanceApprovals;
    this.currentApprovalStage = args.currentApprovalStage;
    this.arrangementRequest = args.arrangementRequest;
    this.nonOrderItems = args.nonOrderItems;
    this.receivedAtInTokyo = args.receivedAtInTokyo;
    this.createdAt = args.createdAt;
  }

  get dateRange() {
    return `${this.cacheStartTime.format('YYYY/MM/DD')}〜${this.cacheEndTime.format('YYYY/MM/DD')}`;
  }

  get currentApproval() {
    return this.inAdvanceApprovals.find(
      inAdvanceApproval => inAdvanceApproval.approveStage === this.currentApprovalStage
    );
  }

  get currentApprovalStatus() {
    return this.currentApproval?.status;
  }

  get nextApproval() {
    return this.inAdvanceApprovals.find(
      inAdvanceApproval => inAdvanceApproval.approveStage === this.currentApprovalStage + 1
    );
  }

  get totalPrice() {
    return (this.order?.totalPrice || 0) + this.nonOrderItemsTotalPrice;
  }

  get nonOrderItemsTotalPrice() {
    return this.nonOrderItems.reduce((acc, nonOrderItem) => acc + nonOrderItem.totalPrice, 0);
  }

  get oldTotalPrice() {
    return (this.firstOrder?.totalPrice || 0) + this.nonOrderItemsTotalPrice;
  }

  get didTotalPriceChange() {
    return this.totalPrice !== this.oldTotalPrice;
  }

  get isDepartmentMappingsExist() {
    return this.order?.orderItems.some(item =>
      item.orderItemMappings.some(mapping => mapping.isChargingDepartmentExist)
    );
  }

  get isDepartmentNonOrderItemMappingsExist() {
    return this.nonOrderItems.some(item =>
      item.nonOrderItemMappings.some(mapping => mapping.isChargingDepartmentExist)
    );
  }

  get isProjectMappingsExist() {
    return this.order?.orderItems.some(item => item.orderItemMappings.some(mapping => mapping.isProjectExist));
  }

  get isProjectNonOrderItemMappingsExist() {
    return this.nonOrderItems.some(item => item.nonOrderItemMappings.some(mapping => mapping.isProjectExist));
  }

  get isExpensesAccountTypeMappingsExist() {
    return this.order?.orderItems.some(item =>
      item.orderItemMappings.some(mapping => mapping.isExpensesAccountTypeExist)
    );
  }

  get isExpensesAccountTypeNonOrderItemMappingsExist() {
    return this.nonOrderItems.some(item =>
      item.nonOrderItemMappings.some(mapping => mapping.isExpensesAccountTypeExist)
    );
  }

  get isHotelCheckinExpired() {
    return this.order?.orderItems.some(item =>
      item.hotels.some(hotel => hotel.tooLate || (hotel.checkinDate && hotel.checkinDate.isSameOrBefore(moment())))
    );
  }

  get flightTooLateError() {
    return this.flightApproveExpiredAt && moment().isSameOrAfter(this.flightApproveExpiredAt);
  }

  get isApproveCanceled() {
    return this.inAdvanceApprovals.some(approver => approver.status === 3);
  }

  get cancelReason() {
    const cancelApproval = this.inAdvanceApprovals.find(approver => approver.status === 3);
    return cancelApproval?.cancelReason;
  }

  isBackStageApprover(userId: number) {
    const approvers = this.inAdvanceApprovals.filter(approver => approver.approverId === userId);
    return approvers.length > 0 && approvers.some(approver => approver.approveStage < this.currentApprovalStage);
  }

  approveReason(num: number) {
    return this.inAdvanceApprovals[num].approveReason;
  }

  rejectReason(num: number) {
    return this.inAdvanceApprovals[num].rejectReason;
  }
}
